import React, { useState } from 'react';
import { Heart, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { simulatorQuestions } from '../../data/simulatorData';

const SimulatorQuestions = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const question = simulatorQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / simulatorQuestions.length) * 100;

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleNext = () => {
    if (!selectedOption) return;

    setIsTransitioning(true);
    
    const newAnswers = [...answers, { 
      questionId: question.id, 
      category: question.category,
      ...selectedOption 
    }];
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentQuestion < simulatorQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        onComplete(newAnswers);
      }
      setIsTransitioning(false);
    }, 400);
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion - 1);
        setSelectedOption(answers[currentQuestion - 1] || null);
        setAnswers(answers.slice(0, -1));
        setIsTransitioning(false);
      }, 300);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#FAF9F7]/90 backdrop-blur-sm">
        <div className="h-1 bg-[#F5F3F0]">
          <div 
            className="h-full bg-gradient-to-r from-[#C4B5A0] to-[#B5C4B1] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-[#8B7D6B]">
            Pergunta {currentQuestion + 1} de {simulatorQuestions.length}
          </span>
          <span className="text-sm text-[#C4B5A0]">
            {question.category}
          </span>
        </div>
      </div>

      {/* Question Content */}
      <div className="flex-1 flex items-center justify-center pt-24 pb-32 px-6">
        <div 
          className={`max-w-2xl w-full transition-all duration-400 ${
            isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
          }`}
        >
          {/* Question */}
          <div className="text-center mb-12">
            <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center mx-auto mb-6">
              <Heart size={24} className="text-[#6B4E37]" />
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#2C2C2C] mb-4 leading-tight">
              {question.question}
            </h2>
            <p className="text-[#8B7D6B] text-lg">
              {question.subtitle}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4">
            {question.options.map((option, index) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(option)}
                className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-300 group ${
                  selectedOption?.id === option.id
                    ? 'border-[#C4B5A0] bg-[#C4B5A0]/10 shadow-md'
                    : 'border-[#E8E4E0] bg-white/50 hover:border-[#D4C4B0] hover:bg-white/80'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    selectedOption?.id === option.id
                      ? 'border-[#C4B5A0] bg-[#C4B5A0] text-white'
                      : 'border-[#D4C4B0] text-[#8B7D6B] group-hover:border-[#C4B5A0]'
                  }`}>
                    {option.id.toUpperCase()}
                  </span>
                  <span className={`text-lg transition-colors duration-300 ${
                    selectedOption?.id === option.id
                      ? 'text-[#2C2C2C]'
                      : 'text-[#6B4E37]'
                  }`}>
                    {option.text}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#FAF9F7]/90 backdrop-blur-sm border-t border-[#E8E4E0]">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className={`flex items-center gap-2 text-[#8B7D6B] transition-all duration-300 ${
              currentQuestion === 0 
                ? 'opacity-0 cursor-default' 
                : 'hover:text-[#6B4E37]'
            }`}
          >
            <ArrowLeft size={20} />
            Anterior
          </button>

          <button
            onClick={handleNext}
            disabled={!selectedOption}
            className={`flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
              selectedOption
                ? 'bg-[#C4B5A0] text-white hover:bg-[#6B4E37] shadow-md hover:shadow-lg'
                : 'bg-[#E8E4E0] text-[#8B7D6B] cursor-not-allowed'
            }`}
          >
            {currentQuestion === simulatorQuestions.length - 1 ? (
              <>
                Ver Resultado
                <Sparkles size={20} />
              </>
            ) : (
              <>
                Continuar
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimulatorQuestions;
